import { getModulePath } from "@/apps/getPath";
import { decisionModule } from "@/apps/management/decision/module";
import { asortymentModule } from "@/apps/plm/asortyment/module";
import { brandModule } from "@/apps/plm/brand/module";
import { czescBudynkuModule } from "@/apps/plm/czescBudynku/module";
import { ingredientGroupModule } from "@/apps/plm/ingredientGroup/module";
import { jednostkaSprzedazyModule } from "@/apps/plm/jednostkaSprzedazy/module";
import { komponentModule } from "@/apps/plm/komponent/module";
import { languageModule } from "@/apps/plm/language/module";
import { narzedzieModule } from "@/apps/plm/narzedzie/module";
import { obiektZastosowaniaModule } from "@/apps/plm/obiektZastosowania/module";
import { opakowanieModule } from "@/apps/plm/opakowanie/module";
import { podlozeModule } from "@/apps/plm/podloze/module";
import { productGroupModule } from "@/apps/plm/productGroup/module";
import { productionRecipeModule } from "@/apps/plm/productionRecipe/module";
import { productListModule } from "@/apps/plm/productList/module";
import { produktModule } from "@/apps/plm/produkt/module";
import { recepturaModule } from "@/apps/plm/receptura/module";
import { rodzajKomponentuModule } from "@/apps/plm/rodzajKomponentu/module";
import { rodzajOpakowaniaModule } from "@/apps/plm/rodzajOpakowania/module";
import { rozpuszczalnikModule } from "@/apps/plm/rozpuszczalnik/module";
import { wyrobModule } from "@/apps/plm/wyrob/module";
import { AppModule } from "@/shared/app";

export interface NavSidebarLinkOptions {
  title: string;
  iconClass?: string;
  path: string;
  external?: boolean;
}
export interface NavSidebarLinkGroupOptions {
  title: string;
  iconClass?: string;
  children: NavSidebarLinkOptions[];
}

export interface NavSidebarSectionOptions {
  title: string;
  children: (NavSidebarLinkOptions | NavSidebarLinkGroupOptions)[];
}

export type NavSidebarOptions = NavSidebarSectionOptions[];

export const moduleSidebarLink = (module: AppModule): NavSidebarLinkOptions => ({
  title: module.title,
  iconClass: module.iconClass,
  path: getModulePath(module),
});

const filterFalse = <T>(array: (T | false)[]) => array.filter((x) => x !== false) as T[];

export const getNavSidebarOptions = (accessibleServices: string[]): NavSidebarOptions => {
  const canView = (serviceName: string) => accessibleServices.includes(serviceName);

  const getLink = (permission: string, module: AppModule) => canView(permission) && moduleSidebarLink(module);

  const getExternalLink = (permission: string, options: { path: string; title: string; iconClass: string }) =>
    canView(permission) && { ...options, external: true };

  const plmNavSection: NavSidebarSectionOptions = {
    title: "PLM",
    children: filterFalse([
      getLink("plm.komponent", komponentModule),
      getLink("plm.receptura", recepturaModule),
      getLink("plm.produkt", produktModule),
      getLink("plm.productList", productListModule),
      getLink("plm.wyrob", wyrobModule),
      getLink("plm.asortyment", asortymentModule),
      getLink("plm.productionRecipe", productionRecipeModule),
    ]),
  };

  const crmNavSection: NavSidebarSectionOptions = {
    title: "CRM",
    children: filterFalse([
      getExternalLink("crm.customer", {
        iconClass: "fas fa-map-marked fa-fw",
        path: "https://v2.megarax.net/crm/klienci",
        title: "Klienci",
      }),
      getExternalLink("crm.salesperson", {
        iconClass: "fas fa-address-card fa-fw",
        path: "https://v2.megarax.net/crm/handlowcy",
        title: "Handlowcy",
      }),
      getExternalLink("crm.route", {
        iconClass: "fas fa-route fa-fw",
        path: "https://v2.megarax.net/crm/trasy",
        title: "Trasy handlowe",
      }),
      getExternalLink("priceReport.myPriceReport", {
        iconClass: "fas fa-poll-h fa-fw",
        path: "https://v2.megarax.net/crm/moje_ankiety_cenowe",
        title: "Moje ankiety cenowe",
      }),
      getExternalLink("priceReport.subjects", {
        iconClass: "fas fa-cog fa-fw",
        path: "https://v2.megarax.net/ankiety_cenowe/produkty",
        title: "Produkty do ankiet",
      }),
    ]),
  };

  const profitMarginNavSection: NavSidebarSectionOptions = {
    title: "Marże",
    children: filterFalse([
      getExternalLink("profitMargin.regionMargins", {
        iconClass: "fas fa-tachometer-alt fa-fw",
        path: "https://v2.megarax.net/marze/realizacja",
        title: "Realizacja planu",
      }),
      getExternalLink("profitMargin.targets", {
        iconClass: "fas fa-cog fa-fw",
        path: "https://v2.megarax.net/marze/plan",
        title: "Plan marży",
      }),
      getExternalLink("profitMargin.costs", {
        iconClass: "fas fa-cog fa-fw",
        path: "https://v2.megarax.net/marze/koszty_standardowe",
        title: "Koszty standardowe",
      }),
      getExternalLink("profitMargin.discounts", {
        iconClass: "fas fa-cog fa-fw",
        path: "https://v2.megarax.net/marze/rabaty_posprzedazowe",
        title: "Rabaty posprzedażowe",
      }),
    ]),
  };

  const pricingNavSection: NavSidebarSectionOptions = {
    title: "Oferta",
    children: filterFalse([
      getExternalLink("pricing.tradeItem", {
        iconClass: "fas fa-barcode fa-fw",
        path: "https://v2.megarax.net/oferta/jednostki_handlowe",
        title: "Jednostki handlowe",
      }),
      getExternalLink("pricing.priceList", {
        iconClass: "fas fa-dollar-sign fa-fw",
        path: "https://v2.megarax.net/oferta/cenniki",
        title: "Cenniki",
      }),
    ]),
  };

  const salesNavSection: NavSidebarSectionOptions = {
    title: "Sprzedaż",
    children: filterFalse([
      getExternalLink("sales.myOrder", {
        iconClass: "fas fa-shopping-cart fa-fw",
        path: "https://v2.megarax.net/sprzedaz/moje_zamowienia",
        title: "Moje zamówienia",
      }),
      getExternalLink("sales.order", {
        iconClass: "fas fa-dolly-flatbed fa-fw",
        path: "https://v2.megarax.net/sprzedaz/zamowienia",
        title: "Zamówienia",
      }),
      getExternalLink("sales.productionSchedule", {
        iconClass: "fas fa-calendar-alt fa-fw",
        path: "https://v2.megarax.net/sprzedaz/plan_produkcji",
        title: "Plan produkcji",
      }),
    ]),
  };

  const iamNavSection: NavSidebarSectionOptions = {
    title: "Administracja",
    children: filterFalse([
      getExternalLink("iam.users", {
        iconClass: "fas fa-users fa-fw",
        path: "https://v2.megarax.net/administracja/uzytkownicy",
        title: "Użytkownicy",
      }),
      getExternalLink("iam.groups", {
        iconClass: "fas fa-user-tie fa-fw",
        path: "https://v2.megarax.net/administracja/stanowiska",
        title: "Stanowiska",
      }),
    ]),
  };

  const invoicesNavSection: NavSidebarSectionOptions = {
    title: "Faktury",
    children: filterFalse([
      getExternalLink("invoices.invoiceThreads.myThreads", {
        iconClass: "fas fa-file-invoice fa-fw",
        path: "https://v2.megarax.net/faktury/watki/moje_faktury",
        title: "Moje faktury",
      }),
      getExternalLink("invoices.invoiceThreads.allThreads", {
        iconClass: "fas fa-file-alt fa-fw",
        path: "https://v2.megarax.net/faktury/watki/wszystkie_faktury",
        title: "Wszystkie faktury",
      }),
      getExternalLink("invoices.budgets", {
        iconClass: "fas fa-wallet fa-fw",
        path: "https://v2.megarax.net/faktury/budzety",
        title: "Budżety",
      }),
    ]),
  };

  const miscNavSection: NavSidebarSectionOptions = {
    title: "",
    children: [
      {
        title: "Pozostałe",
        iconClass: "fas fa-ellipsis-h",
        children: filterFalse([
          getLink("plm.brand", brandModule),
          getLink("plm.rodzajKomponentu", rodzajKomponentuModule),
          getLink("plm.ingredientGroup", ingredientGroupModule),
          getLink("plm.productGroup", productGroupModule),
          getLink("plm.obiektZastosowania", obiektZastosowaniaModule),
          getLink("plm.czescBudynku", czescBudynkuModule),
          getLink("plm.narzedzie", narzedzieModule),
          getLink("plm.rozpuszczalnik", rozpuszczalnikModule),
          getLink("plm.rodzajOpakowania", rodzajOpakowaniaModule),
          getLink("plm.podloze", podlozeModule),
          getLink("plm.jednostkaSprzedazy", jednostkaSprzedazyModule),
          getLink("plm.language", languageModule),
          getLink("plm.opakowanie", opakowanieModule),
        ]),
      },
    ],
  };

  return [
    {
      title: "",
      children: filterFalse([
        { path: "/home", title: "Strona główna", iconClass: "fas fa-home" },
        canView("management.decision") && moduleSidebarLink(decisionModule),
      ]),
    },
    pricingNavSection,
    salesNavSection,
    invoicesNavSection,
    crmNavSection,
    profitMarginNavSection,
    plmNavSection,
    iamNavSection,
    miscNavSection,
  ];
};
